import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import Loader from "../../components/Loader/Loader";
import SubHeader from "../../components/SubHeader/SubHeader";
import CustomModal from "../../components/CustomModal/CustomModal";
import InputField from "../../components/InputField/InputField";
import TextAreaField from "../../components/TextAreaField/TextAreaField";
import SelectInput from "../../components/SelectInput/SelectInput";
import DateField from "../../components/DateField/DateField";
import TimeField from "../../components/TimeField/TimeField";
// import CustomTable from "../../components/CustomTable/CustomTable";
import ResourceCard from "../../components/ResourceCard/ResourceCard";
import ActionIcon from "../../components/ActionIcon/ActionIcon";
import ActionGroup from "../../components/ActionGroup/ActionGroup";
import { EyeTwoTone, EditTwoTone, DeleteTwoTone, InfoCircleTwoTone, ClockCircleTwoTone, FileTextOutlined } from "@ant-design/icons";
import { getPermissionForModule } from "../../services/permissions";
import { getStoredBranchOptions } from "../../services/auth";
import { Popover, Checkbox } from "antd";

import "./Assessment.scss";
import {
  ADD_ASSESSMENTTYPE,
  GET_ASSESSMENTTYPES,
  UPDATE_ASSESSMENTTYPE,
  DELETE_ASSESSMENTTYPE,
  ADD_ASSESSMENTTYPE_SCHEDULE,
} from "../../utils/apiPath";
import { deleteApi, getApi, postApi } from "../../utils/apiService";
import { errorToast, successToast } from "../../services/ToastHelper";
import { formatDateDDMMYYYY } from "../../services/dateFormatter";
import { useNavigate } from "react-router-dom";

// Single source of truth for report types.
// `tone` drives the card highlight colour so the report being generated is recognisable at a glance.
const ASSESSMENT_REPORT_TYPES = [
  { label: "Admission", value: 1, tone: "blue" },
  { label: "Ampere Mathematics", value: 2, tone: "geekblue" },
  { label: "Ampere English", value: 3, tone: "cyan" },
  { label: "Ampere Science / EVS", value: 4, tone: "green" },
  { label: "Psychometric Tamanna", value: 5, tone: "purple" },
  { label: "Psychometric Pathway ", value: 6, tone: "magenta" },
  { label: "Common Assessment", value: 7, tone: "gold" },
  { label: "Career Psychometric", value: 8, tone: "volcano" },
];

// antd Select only needs label/value - keep extra metadata out of the option objects.
const REPORT_TYPE_OPTIONS = ASSESSMENT_REPORT_TYPES.map(({ label, value }) => ({
  label: label.trim(),
  value,
}));

const REPORT_TYPE_MAP = ASSESSMENT_REPORT_TYPES.reduce((acc, item) => {
  acc[item.value] = item;
  return acc;
}, {});

// Resolves the report type of a record regardless of the casing the API returns.
const getReportTypeMeta = (record) => {
  const raw = record?.reportType ?? record?.ReportType;
  if (raw === null || raw === undefined || raw === "") return null;

  const value = Number(raw);
  const match = REPORT_TYPE_MAP[value];
  return {
    label: (match?.label || `Report Type ${raw}`).trim(),
    tone: match?.tone || "default",
  };
};

const Assessment = () => {
  const [isLoading, setIsLoading] = useState(false);

  // role / branch context
  const role = sessionStorage.getItem("role");
  const isAdmin = role === "Admin";
  const isStaff = role === "Staff";
  const sessionBranchId = useMemo(() => {
    const raw = sessionStorage.getItem("branchId");
    const parsed = raw ? Number(raw) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  }, []);
  // Branches assigned to the logged-in staff
  const staffBranchOptions = useMemo(() => getStoredBranchOptions(), []);
  // Only a staff member assigned to more than one branch picks branches here.
  // Admin -> sends []; BranchAdmin / single-branch Staff -> sends their session branch.
  const showBranchSelect = isStaff && staffBranchOptions.length > 1;

  // Resolve the branchId payload by role (shared by Add/Edit and Schedule):
  // - Admin: [] (applies to all branches)
  // - Staff with multiple branches: the selected branches
  // - BranchAdmin / single-branch Staff: their session branch
  const resolveBranchIdPayload = (selectedBranchIds) => {
    if (isAdmin) return [];
    if (showBranchSelect) return selectedBranchIds || [];
    return sessionBranchId ? [sessionBranchId] : [];
  };

  // modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [branchIds, setBranchIds] = useState([]);
  const [assessmentType, setAssessmentType] = useState("");
  const [subTitle, setSubTitle] = useState("");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [purpose, setPurpose] = useState("");
  const [duration, setDuration] = useState("");
  const [format, setFormat] = useState("");
  const [reportType, setReportType] = useState(null);
  const [generateInstruction, setGenerateInstruction] = useState(false);
  const [scheduledDate, setScheduledDate] = useState(null);
  const [scheduledTime, setScheduledTime] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // schedule modal state (Duration / Scheduled Date / Scheduled Time live here now)
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [scheduleId, setScheduleId] = useState(null);
  const [scheduleName, setScheduleName] = useState("");
  const [scheduleBranchIds, setScheduleBranchIds] = useState([]);
  const [scheduleErrors, setScheduleErrors] = useState({});
  const [isScheduling, setIsScheduling] = useState(false);

  const disabledDate = (current) => current && current < dayjs().startOf("day");

  const formatScheduledDate = (date) =>
    date ? dayjs(date).format("DD MMM YYYY") : null;

  const formatScheduledTime = (time) =>
    time ? dayjs(time, "HH:mm:ss").format("hh:mm A") : null;

  // add/edit mode
  const [mode, setMode] = useState("add"); // "add" | "edit"
  const [editingId, setEditingId] = useState(null);

  // table state
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [assessmentTypesData, setAssessmentTypesData] = useState([]);
  const navigate = useNavigate();
  const assessmentPermission = getPermissionForModule("Assessment Types");

  const fetchAssessmentTypes = async () => {
    setIsLoading(true);
    const { statusCode, data, message } = await getApi(GET_ASSESSMENTTYPES);

    if (statusCode === 200) {
      setAssessmentTypesData(data || []);
    } else {
      errorToast(message);
      console.error(message);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchAssessmentTypes();
  }, []);

  // OPEN ADD
  const handleAddAssessment = () => {
    setMode("add");
    setEditingId(null);
    setAssessmentType("");
    setSubTitle("");
    setSubject("");
    setDescription("");
    setPurpose("");
    setFormat("");
    setReportType(null);
    setGenerateInstruction(false);
    setBranchIds([]);
    setErrors({});
    setIsModalOpen(true);
  };

  // OPEN EDIT
  const handleEdit = (record) => {
    setMode("edit");
    setEditingId(record?.id);
    setAssessmentType(record?.name || record?.assessment_type || "");
    setSubTitle(record?.subTitle || record?.SubTitle || "");
    setSubject(record?.subject || record?.Subject || "");
    setDescription(record?.description || record?.Description || "");
    setPurpose(record?.purpose || record?.Purpose || "");
    setFormat(record?.format || record?.Format || "");
    setReportType(record?.reportType ?? record?.ReportType ?? null);
    setGenerateInstruction(record?.generateInstruction ?? record?.GenerateInstruction ?? false);
    // Prefill assigned branches (multi-select shown only for multi-branch staff)
    const existingBranchIds = Array.isArray(record?.branchId)
      ? record.branchId
      : Array.isArray(record?.branchIds)
        ? record.branchIds
        : Array.isArray(record?.branches)
          ? record.branches.map((b) => b.branchId)
          : [];
    setBranchIds(existingBranchIds);
    setErrors({});
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // OPEN SCHEDULE
  const handleOpenSchedule = (record) => {
    setScheduleId(record?.id);
    setScheduleName(record?.name || record?.assessment_type || "");
    setDuration(
      record?.durationInMin != null
        ? String(record.durationInMin)
        : record?.DurationInMin != null
          ? String(record.DurationInMin)
          : ""
    );
    setScheduledDate(record?.scheduledDate ? dayjs(record.scheduledDate) : null);
    setScheduledTime(record?.scheduledTime ? dayjs(record.scheduledTime, "HH:mm:ss") : null);
    // Prefill assigned branches (multi-select shown only for multi-branch staff)
    const existingBranchIds = Array.isArray(record?.branchId)
      ? record.branchId
      : Array.isArray(record?.branchIds)
        ? record.branchIds
        : Array.isArray(record?.branches)
          ? record.branches.map((b) => b.branchId)
          : [];
    setScheduleBranchIds(existingBranchIds);
    setScheduleErrors({});
    setIsScheduleModalOpen(true);
  };

  const handleCloseScheduleModal = () => {
    setIsScheduleModalOpen(false);
  };

  // SUBMIT SCHEDULE
  const handleSubmitSchedule = async () => {
    const newErrors = {};
    if (!duration.trim()) newErrors.duration = "Duration is required";
    if (!scheduledDate) newErrors.scheduledDate = "Scheduled date is required";
    if (!scheduledTime) newErrors.scheduledTime = "Scheduled time is required";
    if (showBranchSelect && scheduleBranchIds.length === 0) newErrors.branchIds = "Please select at least one branch";
    if (Object.keys(newErrors).length > 0) {
      setScheduleErrors(newErrors);
      return;
    }

    setIsScheduling(true);
    try {
      const payload = {
        assessmentTypeId: scheduleId,
        scheduledDate: dayjs(scheduledDate).format("YYYY-MM-DD"),
        scheduledTime: dayjs(scheduledTime).format("HH:mm:ss"),
        durationInMin: duration.trim(),
        branchIds: resolveBranchIdPayload(scheduleBranchIds),
      };
      const { statusCode, message } = await postApi(ADD_ASSESSMENTTYPE_SCHEDULE, payload);
      if (statusCode === 200) {
        successToast(message);
        setIsScheduleModalOpen(false);
        await fetchAssessmentTypes();
      } else {
        errorToast(message);
      }
    } finally {
      setIsScheduling(false);
    }
  };

  // SUBMIT (ADD/UPDATE)
  const handleSubmit = async () => {
    const trimmed = assessmentType.trim();
    const newErrors = {};
    if (!trimmed) newErrors.assessmentType = "Assessment type is required";
    if (!subTitle.trim()) newErrors.subTitle = "Sub title is required";
    if (!subject.trim()) newErrors.subject = "Subject is required";
    if (!format.trim()) newErrors.format = "Format is required";
    if (reportType === null || reportType === undefined) newErrors.reportType = "Report type is required";
    if (!description.trim()) newErrors.description = "Description is required";
    if (!purpose.trim()) newErrors.purpose = "Purpose is required";
    if (showBranchSelect && branchIds.length === 0) newErrors.branchIds = "Please select at least one branch";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const commonPayload = {
        name: trimmed,
        subTitle: subTitle.trim(),
        subject: subject.trim(),
        description: description.trim(),
        purpose: purpose.trim(),
        format: format.trim(),
        reportType: reportType,
        generateInstruction: generateInstruction,
        branchId: resolveBranchIdPayload(branchIds),
      };

      if (mode === "add") {
        const payload = { ...commonPayload };
        const { statusCode, message } = await postApi(
          ADD_ASSESSMENTTYPE,
          payload,
        );
        if (statusCode === 200) {
          successToast(message);
          setIsModalOpen(false);
          await fetchAssessmentTypes();
        } else {
          errorToast(message);
        }
      } else {
        const payload = { id: editingId, ...commonPayload };
        const { statusCode, message } = await postApi(
          UPDATE_ASSESSMENTTYPE,
          payload,
        );

        if (statusCode === 200) {
          successToast(message);
          setIsModalOpen(false);
          await fetchAssessmentTypes();
        } else {
          errorToast(message);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // DELETE: { id } with Popconfirm
  const handleDelete = async (record) => {
    setIsLoading(true);
    try {
      const { statusCode, message } = await deleteApi(DELETE_ASSESSMENTTYPE, {
        params: { id: record?.id },
      });

      if (statusCode === 200) {
        successToast(message);
        await fetchAssessmentTypes();
      } else {
        errorToast(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleView = (record) => {
    navigate(`/grades/${record?.id}`, {
      state: {
        assessmentTypeName: record?.name || record?.assessment_type,
        subject: record?.subject || record?.Subject || "",
      },
    });
  };



  return (
    <div className="assessment">
      {isLoading && <Loader />}

      <SubHeader
        title="Assessment Types"
        showBack={false}
        showRight={assessmentPermission.canAdd}
        rightActionLabel={assessmentPermission.canAdd ? "Add New Assessment" : undefined}
        onRightClick={assessmentPermission.canAdd ? handleAddAssessment : undefined}
      />

      {/* TABLE */}
      <div className="resource-grid">
        {assessmentTypesData.map((item) => {
          const reportTypeMeta = getReportTypeMeta(item);
          return (
          <ResourceCard
            key={item.id}
            badge={
              reportTypeMeta
                ? {
                  label: reportTypeMeta.label,
                  tone: reportTypeMeta.tone,
                  icon: <FileTextOutlined />,
                }
                : undefined
            }
            title={item.name || item.assessment_type}
            subTitle={`Created: ${formatDateDDMMYYYY(item.createdDate)}`}
            description={(item.subject || item.Subject) ? `Subject: ${item.subject || item.Subject}` : undefined}
            scheduledDate={formatScheduledDate(item.scheduledDate || item.ScheduledDate)}
            scheduledTime={formatScheduledTime(item.scheduledTime || item.ScheduledTime)}
            onClick={assessmentPermission?.canView ? () => handleView(item) : undefined}
            actions={
              <ActionGroup>
                <Popover
                  trigger="hover"
                  placement="topLeft"
                  styles={{ root: { maxWidth: 400 }, body: { padding: "12px 16px" } }}
                  content={
                    <div style={{ width: 360, fontSize: 13 }}>
                      {reportTypeMeta && (
                        <p style={{ marginBottom: 6 }}><strong>Report Type:</strong> {reportTypeMeta.label}</p>
                      )}
                      {(item.subTitle || item.SubTitle) && (
                        <p style={{ marginBottom: 6 }}><strong>Sub Title:</strong> {item.subTitle || item.SubTitle}</p>
                      )}
                      {(item.subject || item.Subject) && (
                        <p style={{ marginBottom: 6 }}><strong>Subject:</strong> {item.subject || item.Subject}</p>
                      )}
                      {(item.description || item.Description) && (
                        <p style={{ marginBottom: 6 }}><strong>Description:</strong> {item.description || item.Description}</p>
                      )}
                      {(item.purpose || item.Purpose) && (
                        <p style={{ marginBottom: 6 }}><strong>Purpose:</strong> {item.purpose || item.Purpose}</p>
                      )}
                      {(item.durationInMin || item.DurationInMin) && (
                        <p style={{ marginBottom: 6 }}><strong>Duration:</strong> {item.durationInMin || item.DurationInMin} Min</p>
                      )}
                      {(item.format || item.Format) && (
                        <p style={{ marginBottom: 0 }}><strong>Format:</strong> {item.format || item.Format}</p>
                      )}
                    </div>
                  }
                  title={<span style={{ fontWeight: 600 }}>{item.name || item.assessment_type}</span>}
                >
                  {assessmentPermission?.canView && (
                    <ActionIcon
                      title=""
                      icon={<InfoCircleTwoTone twoToneColor={["#faad14", "#fffbe6"]} />}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </Popover>
                {assessmentPermission?.canView && (
                  <ActionIcon
                    title="View Grades"
                    icon={<EyeTwoTone twoToneColor={["#0e82fd", "#e6f4ff"]} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleView(item);
                    }}
                  />
                )}
                {assessmentPermission?.canEdit && (
                  <ActionIcon
                    title="Schedule"
                    icon={<ClockCircleTwoTone twoToneColor={["#722ed1", "#f9f0ff"]} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenSchedule(item);
                    }}
                  />
                )}
                {assessmentPermission?.canEdit && (
                  <ActionIcon
                    title="Edit"
                    icon={<EditTwoTone twoToneColor={["#52c41a", "#f6ffed"]} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(item);
                    }}
                  />
                )}
                {assessmentPermission?.canDelete && (
                  <ActionIcon
                    title="Delete"
                    icon={<DeleteTwoTone twoToneColor={["#ff4d4f", "#fff1f0"]} />}
                    confirm={{
                      enabled: true,
                      title: `Delete "${item.name || item.assessment_type}"?`,
                      okText: "Delete",
                      cancelText: "Cancel",
                      onConfirm: () => handleDelete(item),
                    }}
                  />
                )}
              </ActionGroup>
            }
          />
          );
        })}
        {assessmentTypesData.length === 0 && !isLoading && (
          <div className="no-data">No assessment types found</div>
        )}
      </div>

      {/* MODAL */}
      <CustomModal
        open={isModalOpen}
        title={mode === "edit" ? "Edit Assessment Type" : "Add Assessment Type"}
        onClose={handleCloseModal}
        dangerText="Cancel"
        primaryText={mode === "edit" ? "Update" : "Submit"}
        onPrimary={handleSubmit}
        onDanger={handleCloseModal}
        primaryProps={{ loading: isSubmitting }}
        width={800}
      >
        <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
          <div style={{ flex: 1 }}>
            <InputField
              title="Assessment Title"
              placeholder="Enter assessment title"
              value={assessmentType}
              required
              onChange={(e) => {
                setAssessmentType(e.target.value);
                if (errors.assessmentType) setErrors((prev) => ({ ...prev, assessmentType: "" }));
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSubmit();
              }}
              errorText={errors.assessmentType || ""}
              helperText="Example: Technical, Behavioral, Aptitude"
            />
          </div>
          <div style={{ flex: 1 }}>
            <InputField
              title="Sub Title"
              placeholder="Enter sub title"
              value={subTitle}
              required
              onChange={(e) => {
                setSubTitle(e.target.value);
                if (errors.subTitle) setErrors((prev) => ({ ...prev, subTitle: "" }));
              }}
              errorText={errors.subTitle || ""}
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
          <div style={{ flex: 1 }}>
            <InputField
              title="Subject"
              placeholder="Enter subject"
              value={subject}
              required
              onChange={(e) => {
                setSubject(e.target.value);
                if (errors.subject) setErrors((prev) => ({ ...prev, subject: "" }));
              }}
              errorText={errors.subject || ""}
            />
          </div>
          <div style={{ flex: 1 }}>
            <InputField
              title="Format"
              placeholder="e.g. MCQ, Written, Oral"
              value={format}
              required
              onChange={(e) => {
                setFormat(e.target.value);
                if (errors.format) setErrors((prev) => ({ ...prev, format: "" }));
              }}
              errorText={errors.format || ""}
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
          <div style={{ flex: 1 }}>
            <SelectInput
              title="Report Type"
              placeholder="Select report type"
              value={reportType}
              options={REPORT_TYPE_OPTIONS}
              required
              onChange={(val) => {
                setReportType(val ?? null);
                if (errors.reportType) setErrors((prev) => ({ ...prev, reportType: "" }));
              }}
              errorText={errors.reportType || ""}
            />
          </div>
          {showBranchSelect && (
            <div style={{ flex: 1 }}>
              <SelectInput
                title="Branch"
                placeholder="Select branch(es)"
                mode="multiple"
                value={branchIds}
                options={staffBranchOptions}
                required
                onChange={(val) => {
                  setBranchIds(val || []);
                  if (errors.branchIds) setErrors((prev) => ({ ...prev, branchIds: "" }));
                }}
                errorText={errors.branchIds || ""}
              />
            </div>
          )}
        </div>

        <div style={{ marginBottom: "16px" }}>
          <Checkbox
            checked={generateInstruction}
            onChange={(e) => setGenerateInstruction(e.target.checked)}
          >
            <strong>Generate Instruction</strong>
          </Checkbox>
        </div>

        <TextAreaField
          title="Description"
          placeholder="Enter a brief description of this assessment type"
          value={description}
          rows={3}
          required
          onChange={(e) => {
            setDescription(e.target.value);
            if (errors.description) setErrors((prev) => ({ ...prev, description: "" }));
          }}
          errorText={errors.description || ""}
        />

        <TextAreaField
          title="Purpose"
          placeholder="Enter the purpose of this assessment"
          value={purpose}
          rows={3}
          required
          onChange={(e) => {
            setPurpose(e.target.value);
            if (errors.purpose) setErrors((prev) => ({ ...prev, purpose: "" }));
          }}
          errorText={errors.purpose || ""}
        />
      </CustomModal>

      {/* SCHEDULE MODAL */}
      <CustomModal
        open={isScheduleModalOpen}
        title={scheduleName ? `Schedule - ${scheduleName}` : "Schedule Assessment"}
        onClose={handleCloseScheduleModal}
        dangerText="Cancel"
        primaryText="Save"
        onPrimary={handleSubmitSchedule}
        onDanger={handleCloseScheduleModal}
        primaryProps={{ loading: isScheduling }}
        width={700}
      >
        <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
          <div style={{ flex: 1 }}>
            <InputField
              title="Duration (Min)"
              placeholder="e.g. 30"
              value={duration}
              required
              onChange={(e) => {
                const val = e.target.value;
                if (val === "" || (/^\d+$/.test(val) && Number(val) <= 999)) setDuration(val);
                if (scheduleErrors.duration) setScheduleErrors((prev) => ({ ...prev, duration: "" }));
              }}
              onKeyDown={(e) => {
                if (!/[\d]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              errorText={scheduleErrors.duration || ""}
            />
          </div>
          {showBranchSelect && (
            <div style={{ flex: 1 }}>
              <SelectInput
                title="Branch"
                placeholder="Select branch(es)"
                mode="multiple"
                value={scheduleBranchIds}
                options={staffBranchOptions}
                required
                onChange={(val) => {
                  setScheduleBranchIds(val || []);
                  if (scheduleErrors.branchIds) setScheduleErrors((prev) => ({ ...prev, branchIds: "" }));
                }}
                errorText={scheduleErrors.branchIds || ""}
              />
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
          <div style={{ flex: 1 }}>
            <DateField
              title="Scheduled Date"
              placeholder="DD - MM - YYYY"
              value={scheduledDate}
              required
              disabledDate={disabledDate}
              onChange={(date) => {
                setScheduledDate(date);
                if (scheduleErrors.scheduledDate) setScheduleErrors((prev) => ({ ...prev, scheduledDate: "" }));
              }}
              errorText={scheduleErrors.scheduledDate || ""}
            />
          </div>
          <div style={{ flex: 1 }}>
            <TimeField
              title="Scheduled Time"
              placeholder="HH : MM : SS"
              value={scheduledTime}
              required
              onChange={(time) => {
                setScheduledTime(time);
                if (scheduleErrors.scheduledTime) setScheduleErrors((prev) => ({ ...prev, scheduledTime: "" }));
              }}
              errorText={scheduleErrors.scheduledTime || ""}
            />
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default Assessment;
